import { StreamingShimmer } from '@euler/app/components/message/streaming/StreamingShimmer';
import { Typography } from '@euler/app/components/typography';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { AiAttachmentInfo, ApdmBuildStep } from '@euler/model/apdm';
import { useServiceFactory } from '@euler/services/factory';
import dayjs from 'dayjs';
import * as Clipboard from 'expo-clipboard';
import {
  Check,
  Copy,
  RefreshCw,
  Sparkles,
  ThumbsDown,
  ThumbsUp,
} from 'lucide-react-native';
import { ReactNode, memo, useEffect, useMemo, useState } from 'react';
import { ActivityIndicator, Pressable, StyleSheet, View } from 'react-native';
import Toast from 'react-native-root-toast';
import { OpenableAttachment } from '../attachments/OpenableAttachment';
import {
  AiChatReaction,
  AiChatReplyStatus,
  AiChatStep,
  AiChatMessage as Message,
} from '../conversation';
import { aiColors, tint } from '../theme';
import { AiChatMessageContext } from './AiChatActions';
import {
  ReplyText,
  ReplyWidget,
  isStepAnswerWidget,
  replyParts,
} from './AiReplyContent';

const kMuted = '#94A3B8';

function copyText(text: string) {
  void Clipboard.setStringAsync(text);
  Toast.show('已复制', { duration: 500, position: Toast.positions.CENTER });
}

/** "13:16" today, "10月15日 13:16" on another day. */
function messageTime(createdAt: number): string {
  const time = dayjs(createdAt);
  return time.isSame(dayjs(), 'day')
    ? time.format('HH:mm')
    : time.format('M月D日 HH:mm');
}

export const UserMessage = memo(
  ({ message }: { message: Extract<Message, { role: 'user' }> }) => {
    const { rpx } = useResponsiveContext();
    const styles = useStyles();
    const attachments = useAttachments(
      message.attachments,
      message.attachmentIds,
    );
    // The reply that follows says what happened.
    if (message.event) return null;
    return (
      <View style={styles.userColumn}>
        {message.content ? (
          <View style={styles.userBubble}>
            <Typography.Text
              selectable
              size={rpx(14)}
              lineHeight={rpx(21)}
              color={aiColors.primaryForeground}
            >
              {message.content}
            </Typography.Text>
          </View>
        ) : null}
        {attachments.map(info => (
          <View key={info.id} style={{ maxWidth: '85%' }}>
            <OpenableAttachment info={info} />
          </View>
        ))}
        <View style={styles.userMeta}>
          <Typography.Text size={rpx(10)} color={kMuted}>
            {messageTime(message.createdAt)}
          </Typography.Text>
          {message.content ? (
            <MetaButton label="复制" onPress={() => copyText(message.content)}>
              <Copy size={12} color={kMuted} strokeWidth={2} />
            </MetaButton>
          ) : null}
        </View>
      </View>
    );
  },
);

/** History carries only attachment ids; read their names and sizes once. */
function useAttachments(
  known: AiAttachmentInfo[],
  ids: string[],
): AiAttachmentInfo[] {
  const { aiAttachmentService } = useServiceFactory();
  const [loaded, setLoaded] = useState<AiAttachmentInfo[]>([]);
  const key = ids.join(',');
  useEffect(() => {
    if (!key) return;
    let active = true;
    Promise.all(
      key
        .split(',')
        .map(id => aiAttachmentService.getMetadata(id).catch(() => null)),
    )
      .then(rows => {
        if (active)
          setLoaded(rows.filter((row): row is AiAttachmentInfo => row != null));
      })
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, [aiAttachmentService, key]);
  return known.length ? known : loaded;
}

export const AssistantMessage = memo(
  ({
    message,
    latest,
    answers,
    biUploads,
    working,
    work,
    width,
    onRetry,
    onSuggestion,
    onReact,
  }: {
    message: Extract<Message, { role: 'assistant' }>;
    latest: boolean;
    /** The build steps whose newest card is in this reply. */
    answers: readonly ApdmBuildStep[];
    /** The file uploads whose newest card is in this reply. */
    biUploads: readonly string[];
    working: string | null;
    /** What the reply has done so far, shown while it is written. */
    work: readonly AiChatStep[];
    width: number;
    onRetry: () => void;
    onSuggestion: (text: string) => void;
    onReact: (key: string, reaction: AiChatReaction) => void;
  }) => {
    const { rpx } = useResponsiveContext();
    const styles = useStyles();
    const streaming = message.status === AiChatReplyStatus.Streaming;
    const parts = useMemo(() => replyParts(message.content), [message.content]);
    // A step's card already offers its answers; chips beside it repeat them.
    const holdsAnswers = parts.segments.some(segment =>
      segment.kind === 'widget' ? isStepAnswerWidget(segment) : false,
    );
    const info = useMemo(
      () => ({ latest, answers, biUploads }),
      [latest, answers, biUploads],
    );
    const finished = message.status === AiChatReplyStatus.Done;
    const failed =
      message.status === AiChatReplyStatus.Failed ||
      message.status === AiChatReplyStatus.Stopped;
    const shownWork = streaming ? work : [];
    // A step still running shows its own spinner; the shimmer is for the
    // model deciding, between and after them.
    const busyStep = shownWork.some(step => step.done < step.total);
    const thinking =
      streaming && !busyStep && (!parts.segments.length || working != null)
        ? shownWork.length
          ? '正在思考'
          : working ?? '正在思考'
        : null;
    // A timeline, as it happened: words, the lines of what was settled
    // and looked up, and each card after the words about it; when and how
    // it was said comes last.
    // Cards take the column's width; the avatar sits beside it.
    const columnWidth = Math.max(0, width - rpx(36));
    const copy = () => copyText(parts.text);
    return (
      <AiChatMessageContext.Provider value={info}>
        <View style={styles.assistantRow}>
          <View style={styles.avatar}>
            <Sparkles size={14} color={aiColors.primary} strokeWidth={2} />
          </View>
          <View style={styles.assistantColumn}>
            {shownWork.length ? (
              <View accessibilityLiveRegion="polite" style={styles.work}>
                {shownWork.map(step => (
                  <WorkLine key={step.label} step={step} />
                ))}
              </View>
            ) : null}
            {parts.segments.map((segment, index) =>
              segment.kind === 'text' ? (
                <View key={index} style={styles.assistantBubble}>
                  <ReplyText content={segment.content} />
                </View>
              ) : (
                <ReplyWidget
                  key={index}
                  widget={segment.widget}
                  data={segment.data}
                  messageId={message.id ?? message.key}
                  blockIndex={index}
                  streaming={streaming}
                  live={message.live}
                  width={columnWidth}
                />
              ),
            )}
            {thinking ? (
              <View
                accessibilityLiveRegion="polite"
                style={styles.assistantBubble}
              >
                <StreamingShimmer>
                  {thinking.replace(/…$/, '')}
                </StreamingShimmer>
              </View>
            ) : null}
            {!streaming && (
              <View style={styles.meta}>
                <Typography.Text size={rpx(10)} color={kMuted}>
                  {messageTime(message.createdAt)}
                </Typography.Text>
                {finished && parts.text ? (
                  <>
                    <MetaButton label="复制" onPress={copy}>
                      <Copy size={12} color={kMuted} strokeWidth={2} />
                    </MetaButton>
                    {message.id ? (
                      <>
                        <MetaButton
                          label="有帮助"
                          active={message.reaction === 'up'}
                          onPress={() => onReact(message.key, 'up')}
                        >
                          <ThumbsUp
                            size={12}
                            strokeWidth={2}
                            color={
                              message.reaction === 'up'
                                ? aiColors.primary
                                : kMuted
                            }
                            fill={
                              message.reaction === 'up'
                                ? aiColors.primary
                                : 'none'
                            }
                          />
                        </MetaButton>
                        <MetaButton
                          label="没帮助"
                          active={message.reaction === 'down'}
                          onPress={() => onReact(message.key, 'down')}
                        >
                          <ThumbsDown
                            size={12}
                            strokeWidth={2}
                            color={
                              message.reaction === 'down'
                                ? aiColors.red
                                : kMuted
                            }
                            fill={
                              message.reaction === 'down'
                                ? aiColors.red
                                : 'none'
                            }
                          />
                        </MetaButton>
                      </>
                    ) : null}
                  </>
                ) : null}
                {failed && (
                  <Typography.Text
                    size={rpx(11)}
                    color={
                      message.status === AiChatReplyStatus.Failed
                        ? aiColors.destructive
                        : kMuted
                    }
                  >
                    {message.status === AiChatReplyStatus.Failed
                      ? '回复没有完成'
                      : '已停止'}
                  </Typography.Text>
                )}
                {failed && latest && (
                  <Pressable
                    accessibilityRole="button"
                    onPress={onRetry}
                    style={styles.retry}
                  >
                    <RefreshCw
                      size={11}
                      color={aiColors.primary}
                      strokeWidth={2.5}
                    />
                    <Typography.Text
                      medium
                      size={rpx(11)}
                      color={aiColors.primary}
                    >
                      重试
                    </Typography.Text>
                  </Pressable>
                )}
              </View>
            )}
            {latest &&
              finished &&
              !holdsAnswers &&
              parts.suggestions.length > 0 && (
                <View style={styles.suggestions}>
                  {parts.suggestions.map(text => (
                    <Pressable
                      key={text}
                      accessibilityRole="button"
                      onPress={() => onSuggestion(text)}
                      style={({ pressed }) => [
                        styles.suggestion,
                        pressed && { opacity: 0.7 },
                      ]}
                    >
                      <Typography.Text
                        size={rpx(12)}
                        color={aiColors.foreground}
                      >
                        {text}
                      </Typography.Text>
                    </Pressable>
                  ))}
                </View>
              )}
          </View>
        </View>
      </AiChatMessageContext.Provider>
    );
  },
);

/** One line of the reply's work: a spinner while it runs, ✓ once done. */
const WorkLine = memo(({ step }: { step: AiChatStep }) => {
  const { rpx } = useResponsiveContext();
  const done = step.done >= step.total;
  const text = done
    ? step.label.replace(/^正在/, '').replace(/…$/, '')
    : step.label.replace(/…$/, '');
  return (
    <View style={{ flexDirection: 'row', alignItems: 'center', gap: rpx(6) }}>
      {done ? (
        <Check size={13} color={aiColors.green} strokeWidth={2.5} />
      ) : (
        <ActivityIndicator size="small" color={kMuted} />
      )}
      <Typography.Text size={rpx(12)} color={kMuted}>
        {step.total > 1 ? `${text} ×${step.total}` : text}
      </Typography.Text>
    </View>
  );
});

const MetaButton = memo(
  ({
    label,
    active,
    onPress,
    children,
  }: {
    label: string;
    active?: boolean;
    onPress: () => void;
    children: ReactNode;
  }) => {
    const styles = useStyles();
    return (
      <Pressable
        accessibilityRole="button"
        accessibilityLabel={label}
        accessibilityState={{ selected: !!active }}
        hitSlop={6}
        onPress={onPress}
        style={[styles.metaButton, active && styles.metaButtonActive]}
      >
        {children}
      </Pressable>
    );
  },
);

function useStyles() {
  const { rpx } = useResponsiveContext();
  return useMemo(
    () =>
      StyleSheet.create({
        userColumn: { alignItems: 'flex-end', gap: rpx(6) },
        userMeta: { flexDirection: 'row', alignItems: 'center', gap: rpx(8) },
        work: { gap: rpx(6), paddingVertical: rpx(2) },
        userBubble: {
          maxWidth: '85%',
          paddingHorizontal: rpx(14),
          paddingVertical: rpx(10),
          borderRadius: rpx(16),
          borderTopRightRadius: rpx(4),
          backgroundColor: aiColors.primary,
        },
        assistantRow: { flexDirection: 'row', alignItems: 'flex-start' },
        avatar: {
          width: rpx(28),
          height: rpx(28),
          borderRadius: rpx(14),
          marginRight: rpx(8),
          marginTop: rpx(2),
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: tint(aiColors.primary, 0.08),
        },
        assistantColumn: { flex: 1, gap: rpx(8), alignItems: 'stretch' },
        assistantBubble: {
          alignSelf: 'flex-start',
          maxWidth: '100%',
          paddingHorizontal: rpx(14),
          paddingVertical: rpx(10),
          borderRadius: rpx(16),
          borderTopLeftRadius: rpx(4),
          backgroundColor: '#F1F5F9',
        },
        meta: {
          flexDirection: 'row',
          alignItems: 'center',
          gap: rpx(8),
          paddingHorizontal: rpx(2),
        },
        metaButton: { padding: rpx(3), borderRadius: rpx(6) },
        metaButtonActive: { backgroundColor: tint(aiColors.primary, 0.08) },
        retry: { flexDirection: 'row', alignItems: 'center', gap: rpx(3) },
        suggestions: { alignItems: 'flex-start', gap: rpx(8) },
        suggestion: {
          paddingHorizontal: rpx(12),
          paddingVertical: rpx(7),
          borderRadius: rpx(16),
          borderWidth: 1,
          borderColor: aiColors.border,
          backgroundColor: aiColors.card,
        },
      }),
    [rpx],
  );
}
